import { useEffect, useRef, useState } from "react";

interface UseEventSourceResult<T = string> {
  data: T | null;
  error: Event | Error | null;
  isLoading: boolean;
  close: () => void;
}

type AnyRecord = Record<string, unknown>;

type EventControls = { close: () => void };

type EventHandlers<M extends AnyRecord, E = string> = {
  [K in keyof M]: (
    data: M[K],
    event: MessageEvent<E>,
    controls?: EventControls
  ) => void | Promise<void>;
};

type EventSourceOptions<M extends AnyRecord, E = string> = {
  eventKey?: string | Array<unknown>;
  url: string;
  enabled?: boolean;
  eventHandlers: EventHandlers<M, E>;
  onError?: (error: Event | Error) => void;
  onOpen?: () => void;
  onClose?: () => void;
  /** Close the EventSource automatically when receiving these events. */
  closeOn?: Array<keyof M & string>;
  /**
   * Fallback parser used when a per-event parser is not provided.
   * If omitted, raw `event.data` is passed through as-is (string).
   */
  parse?: (data: string) => M[keyof M];
  retryInterval?: number;
};

/**
 * NOTE: Auto reconnect can be controlled using `enabled` and `closeOn`.
 *
 * useEventSource is a React hook for consuming Server-Sent Events (SSE) with per-event handlers and optional parsing.
 *
 * @template M - Mapping of event name to payload type (default: Record<string, string>)
 * @param {Object} options - Options for the hook
 * @param {string} options.eventKey - The EventSource endpoint URL
 * @param {string} options.url - The EventSource endpoint URL
 * @param {boolean} [options.enabled=true] - Whether to enable the EventSource connection
 * @param {Object} options.eventHandlers - Object mapping event names to handler functions (data, event)
 * @param {(error: Event | Error) => void} [options.onError] - Callback when an error occurs
 * @param {() => void} [options.onClose] - Callback when the connection is closed
 * @param {string[]} [options.closeOn] - Event names that should close the stream when received
 * @param {(data: string) => M[keyof M]} [options.parser] - Function to transform raw event data for all events
 * @param {number} options.retryInterval - Time in milliseconds to wait before retrying the connection
 *
 * @returns {{ data: M[keyof M] | null, error: Event | Error | null, isLoading: boolean }}
 *
 * @example
 * // Single payload type for all events (back-compat):
 * useEventSource<Record<string, {foo: string}>>({
 *   url: "/api/events",
 *   eventHandlers: {
 *     "analysis-list": (data, event) => { ... },
 *     "dataset_data:done": (data, event) => { ... },
 *     "stream:error": (data, event) => { ... }
 *   },
 *   parse: JSON.parse
 * });
 *
 * @example
 * // Per-event payload types using a single JSON parser for all events:
 * type Events = {
 *   "dataset_operation_status": OperationStatus;
 *   "dataset_data:done": { dataset_id: string };
 *   "stream:error": { message: string };
 * };
 * useEventSource<Events>({
 *   url: "/api/events",
 *   eventHandlers: {
 *     dataset_operation_status: (data) => data,
 *     "dataset_data:done": (data) => data,
 *     "stream:error": (data) => data,
 *   },
 *   parse: JSON.parse
 * });
 */
function useEventSource<
  M extends AnyRecord = Record<string, string>,
  E = unknown
>({
  eventKey,
  url,
  enabled = true,
  eventHandlers,
  onError,
  onOpen,
  onClose,
  closeOn,
  parse,
  retryInterval = 5000
}: EventSourceOptions<M, E>): UseEventSourceResult<M[keyof M]> {
  const [data, setData] = useState<M[keyof M] | null>(null);
  const [error, setError] = useState<Event | Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /** Ref to keep a reference to the previous EventSource instance */
  const eventSourceRef = useRef<EventSource | null>(null);
  const handlersRef = useRef(eventHandlers);
  const parseRef = useRef(parse);
  const closeOnRef = useRef(closeOn);
  const onOpenRef = useRef(onOpen);
  const onErrorRef = useRef(onError);
  const onCloseRef = useRef(onClose);

  // Added refs for reconnect logic
  const shouldReconnectRef = useRef(true);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  handlersRef.current = eventHandlers;
  parseRef.current = parse;
  closeOnRef.current = closeOn;
  onOpenRef.current = onOpen;
  onErrorRef.current = onError;
  onCloseRef.current = onClose;

  let cacheKey: string;
  if (eventKey) {
    cacheKey = Array.isArray(eventKey) ? JSON.stringify(eventKey) : eventKey;
  } else {
    cacheKey = url;
  }

  const close = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      onCloseRef.current?.();
    }
  };

  const closeAndDisableReconnect = () => {
    shouldReconnectRef.current = false; // Disable reconnect
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    close();
  };

  const connect = () => {
    setIsLoading(true);

    const eventSource = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = eventSource;

    const eventHandlersArr = Object.keys(handlersRef.current) as Array<
      keyof M & string
    >;

    const flattenedEventHandlers = eventHandlersArr.map((eventType) => {
      const listener = (event: MessageEvent<E>) => {
        const raw = event?.data ?? "";
        const parser = parseRef.current;
        const parsedForEvent =
          parser && typeof raw === "string" && raw.trim() !== ""
            ? (parser(raw) as M[typeof eventType])
            : (raw as unknown as M[typeof eventType]);
        setData(parsedForEvent);
        setIsLoading(false);

        handlersRef.current?.[eventType]?.(parsedForEvent, event, { close });

        // Short circuit: close the EventSource early if this eventType is in the `closeOn` list
        if (closeOnRef.current?.includes(eventType)) closeAndDisableReconnect();
      };

      eventSource.addEventListener(eventType, listener);
      return { eventType, handler: listener };
    });

    const cleanup = () => {
      flattenedEventHandlers.forEach(({ eventType, handler }) => {
        eventSource.removeEventListener(eventType, handler);
      });
    };

    const scheduleReconnect = () => {
      if (!shouldReconnectRef.current) return;

      retryTimeoutRef.current = setTimeout(() => {
        connect();
      }, retryInterval);
    };

    const handleError = (event: Event) => {
      setError(event);
      setIsLoading(false);
      onErrorRef.current?.(event);

      cleanup(); // remove all event listeners

      // Centralize closing logic via ref-backed close() to ensure onClose fires
      close();

      scheduleReconnect(); // This wouldn't run if we called close()
    };

    eventSource.onerror = handleError;

    eventSource.onopen = () => {
      setIsLoading(false);
      setError(null);
      onOpenRef.current?.();
    };

    return cleanup;
  };

  useEffect(() => {
    if (!enabled) return;

    if (!url) {
      const err = new Error("EventSource URL is not provided.");
      setError(err);
      setIsLoading(false);
      onErrorRef.current?.(err);
      return;
    }

    shouldReconnectRef.current = true; // enable reconnect on new connection

    const cleanupFn = connect();

    return () => {
      closeAndDisableReconnect();
      if (cleanupFn !== undefined) cleanupFn();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, enabled]);

  return { data, error, isLoading, close };
}

export default useEventSource;
